import { useState } from "react";
import { toast } from "sonner";
import { Page, Reveal, MaskedLine } from "@/components/Page";
import { useLang } from "@/i18n";
import { useContent } from "@/content";
import { useSeo } from "@/hooks/useSeo";
import { API } from "@/api";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "", company: "" });
  const [status, setStatus] = useState("idle");
  const { lang, t } = useLang();
  const cms = useContent();
  const seo = cms.site?.seo?.contact || {};
  useSeo({ title: seo.title, description: seo.description });

  const info = cms.site?.contact || {};
  const lines = info.lines?.[lang] || t.contact.lines;

  const FIELDS = [
    { id: "name", label: t.contact.form.name, type: "text", testid: "contact-input-name", textarea: false, required: true },
    { id: "email", label: t.contact.form.email, type: "email", testid: "contact-input-email", textarea: false, required: true },
    { id: "phone", label: t.contact.form.phone, type: "tel", testid: "contact-input-phone", textarea: false, required: false },
    { id: "message", label: t.contact.form.message, type: "text", testid: "contact-input-message", textarea: true, required: true },
  ];

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      const res = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, lang }),
      });
      if (!res.ok) throw new Error("send failed");
      setStatus("success");
      toast.success(t.contact.toast);
      setForm({ name: "", email: "", phone: "", message: "", company: "" });
    } catch {
      setStatus("error");
      toast.error(t.contact.formError);
    }
  };

  const inputClass =
    "w-full border-b border-ink/20 bg-transparent pb-3 text-lg font-light outline-none transition-colors duration-300 focus:border-ink";

  return (
    <Page testid="contact-page" className="bg-bone min-h-screen">
      <section className="px-5 sm:px-10 pt-36 sm:pt-48 pb-24 sm:pb-36">
        <h1 className="max-w-4xl text-2xl sm:text-4xl lg:text-5xl font-light uppercase tracking-tight leading-[1.05]">
          {lines.map((line, i) => (
            <MaskedLine key={i} delay={0.2 + i * 0.15}>{line}</MaskedLine>
          ))}
        </h1>

        <div className="mt-20 sm:mt-32 grid grid-cols-1 lg:grid-cols-12 gap-16">
          <Reveal className="lg:col-span-5 flex flex-col gap-10">
            <div>
              <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-clay">{t.contact.emailLabel}</span>
              <a
                href={`mailto:${info.email || "hello@solidor.studio"}`}
                data-testid="contact-email-link"
                className="mt-3 block text-lg sm:text-xl font-light hover:text-solidor transition-colors duration-300"
              >
                {info.email || "hello@solidor.studio"}
              </a>
            </div>
            <div>
              <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-clay">{t.contact.socialLabel}</span>
              <a
                href={info.instagram || "https://instagram.com"}
                target="_blank"
                rel="noreferrer"
                data-testid="contact-instagram-link"
                className="mt-3 block text-lg sm:text-xl font-light hover:text-solidor transition-colors duration-300"
              >
                Instagram
              </a>
            </div>
            <div>
              <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-clay">{t.contact.studioLabel}</span>
              <p className="mt-3 text-lg sm:text-xl font-light">{info.location?.[lang] || "Istanbul, Türkiye"}</p>
            </div>
          </Reveal>

          <Reveal className="lg:col-span-6 lg:col-start-7" delay={0.15}>
            <form data-testid="contact-form" onSubmit={submit} className="flex flex-col gap-10">
              <input
                type="text"
                name="company"
                value={form.company}
                onChange={(e) => setForm({ ...form, company: e.target.value })}
                className="absolute -left-[9999px] h-0 w-0 opacity-0"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
              />
              {FIELDS.map((f) => (
                <div key={f.id} className="flex flex-col gap-3">
                  <label htmlFor={f.id} className="font-mono text-[10px] uppercase tracking-[0.3em] text-clay">
                    {f.label}{!f.required && ` (${lang === "tr" ? "isteğe bağlı" : "optional"})`}
                  </label>
                  {f.textarea ? (
                    <textarea
                      id={f.id}
                      data-testid={f.testid}
                      required={f.required}
                      rows={5}
                      value={form[f.id]}
                      onChange={(e) => setForm({ ...form, [f.id]: e.target.value })}
                      className={`${inputClass} resize-none`}
                    />
                  ) : (
                    <input
                      id={f.id}
                      data-testid={f.testid}
                      required={f.required}
                      type={f.type}
                      value={form[f.id]}
                      onChange={(e) => setForm({ ...form, [f.id]: e.target.value })}
                      className={inputClass}
                    />
                  )}
                </div>
              ))}
              <div className="mt-4 flex items-center gap-6">
                <button
                  type="submit"
                  data-testid="contact-submit-button"
                  disabled={status === "sending"}
                  className="group inline-flex items-center gap-4 self-start font-mono text-[11px] uppercase tracking-[0.3em] disabled:opacity-50"
                >
                  <span className="border-b border-ink pb-1 transition-opacity duration-300 group-hover:opacity-50">
                    {status === "sending" ? "…" : t.contact.form.send}
                  </span>
                  <span aria-hidden className="transition-transform duration-500 group-hover:translate-x-2">→</span>
                </button>
                {status === "success" && (
                  <span data-testid="contact-success-note" className="text-sm font-light text-solidor">
                    {lang === "tr" ? "Mesajınız gönderildi." : "Your message has been sent."}
                  </span>
                )}
                {status === "error" && (
                  <span data-testid="contact-error-note" className="text-sm font-light text-red-900">
                    {t.contact.formError}
                  </span>
                )}
              </div>
            </form>
          </Reveal>
        </div>
      </section>
    </Page>
  );
}
